import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Col, Row } from 'reactstrap';
import { withStyles } from '@material-ui/core';
import * as Constants from '../Pages/Constants';
import { Icon } from 'semantic-ui-react';
import { FormattedMessage, useIntl } from 'react-intl';
import DataServiceSQL from '../DataService/DataServiceSQL';

/**
 * FixturesMenu — Super-admin page to discover and execute backend fixture scripts.
 *
 * Visible only when:
 *   - user.role_extra === 'admin_etc'
 */

/**
 * Build the initial arg values for a catalog entry:
 *   - apply each arg's declared defaultValue
 *   - pre-fill any --user-id (owner) field with the connected admin's id_user
 *     (so "Create Demo Patients" defaults to the logged-in user, editable).
 *   - on "Clean Patients" / "Clean Patient Data", pre-fill the --user (User
 *     email) filter with the connected admin's email so it defaults to
 *     targeting their own patients (the script resolves the owner by
 *     table_user.username, which equals the email in this app).
 */
function makeDefaults(entry, accountInformation) {
  const defaults = {};
  for (const a of entry.args) {
    if (a.defaultValue !== undefined) defaults[a.flag] = a.defaultValue;
  }
  const acct = accountInformation || {};
  if (acct.id_user && entry.args.some(a => a.flag === '--user-id')) {
    defaults['--user-id'] = acct.id_user;
  }
  if (acct.email
      && (entry.script === 'clean_patients.js' || entry.script === 'clean_patient_data.js')
      && entry.args.some(a => a.flag === '--user')) {
    defaults['--user'] = acct.email;
  }
  return defaults;
}

const ALERT_TYPES = [
  'fever_alert', 'SPO2_alert', 'PR_alert',
  'bloodpressure_PR_alert', 'bloodpressure_sys_high_alert', 'bloodpressure_dia_alert',
  'weight_alert', 'pain_alert',
  'breathing_rate_alert', 'breathing_leakage_alert', 'breathing_leakage95_alert', 'breathing_ahi_alert',
  'detach_alert', 'battery_alert',
];

const REMINDER_TYPES = [
  'temperature', 'spo2', 'bloodpressure', 'weight', 'pain',
  'respiratory_rate', 'respiratory_leak', 'respiratory_ahi',
  'oxygen_flow_rate', 'health_form',
];

const FixturesMenu = (props) => {
  const { classes } = props;
  const intl = useIntl();

  const [activeTab, setActiveTab]   = useState('fixtures');

  const [catalog, setCatalog]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState('');
  const [filter, setFilter]         = useState('');
  const [selected, setSelected]     = useState(null);   // script name
  const [argValues, setArgValues]   = useState({});     // { [flag]: value }
  const [running, setRunning]       = useState(false);
  const [result, setResult]         = useState(null);   // last run result
  const [confirmDanger, setConfirmDanger] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const runStartRef = useRef(null);

  // Actions tab state
  const [alertPatientId, setAlertPatientId]   = useState('');
  const [alertType, setAlertType]             = useState(ALERT_TYPES[0]);
  const [alertValue, setAlertValue]           = useState('');
  const [alertSendSms, setAlertSendSms]       = useState(true);
  const [alertSendMail, setAlertSendMail]     = useState(true);
  const [alertRecall, setAlertRecall]         = useState(true);
  const [alertLang, setAlertLang]             = useState('fr-FR');
  const [alertRunning, setAlertRunning]       = useState(false);
  const [alertResult, setAlertResult]         = useState(null);

  const [reminderPatientId, setReminderPatientId] = useState('');
  const [reminderType, setReminderType]           = useState(REMINDER_TYPES[0]);
  const [reminderSendSms, setReminderSendSms]     = useState(true);
  const [reminderSendMail, setReminderSendMail]   = useState(true);
  const [reminderRecall, setReminderRecall]       = useState(true);
  const [reminderLang, setReminderLang]           = useState('fr-FR');
  const [reminderRunning, setReminderRunning]     = useState(false);
  const [reminderResult, setReminderResult]       = useState(null);

  // Load catalog on mount, then auto-select "Create Demo Patients"
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const res = await DataServiceSQL.getAdminFixtures();
        if (!alive) return;
        const catalogData = res.data.catalog || [];
        setCatalog(catalogData);
        // Pre-select the first entry (catalog is ordered: create_demo_patients first)
        const first = catalogData[0];
        if (first) {
          setSelected(first.script);
          setArgValues(makeDefaults(first, props.accountInformation));
        }
      } catch (e) {
        if (!alive) return;
        const status = e && e.response && e.response.status;
        if (status === 404) {
          setLoadError(intl.formatMessage({ id: 'admin.fixtures.error.not_debug', defaultMessage: 'Service fixtures indisponible.' }));
        } else if (status === 403 || status === 401) {
          setLoadError(intl.formatMessage({ id: 'admin.fixtures.error.forbidden', defaultMessage: 'Accès refusé.' }));
        } else {
          setLoadError(intl.formatMessage({ id: 'admin.fixtures.error.load', defaultMessage: 'Erreur de chargement du catalogue.' }));
        }
      }
      setLoading(false);
    })();
    return () => { alive = false; };
    // Load the catalog once, on mount only. Re-running this (e.g. if `intl`
    // identity changed) would re-fetch and reset the selection to the first
    // tab while the admin is using the page.
    // Bare disable (no rule name): suppresses react-hooks/exhaustive-deps where
    // the plugin is loaded (CRA dev/build), without erroring in environments
    // where eslint-plugin-react-hooks isn't resolvable ("Definition for rule
    // 'react-hooks/exhaustive-deps' was not found" on some staging/CI builds).
    // eslint-disable-next-line
  }, []);

  // Live elapsed-time counter while a script is running
  useEffect(() => {
    if (!running) { setElapsed(0); return; }
    runStartRef.current = Date.now();
    setElapsed(0);
    const t = setInterval(() => {
      setElapsed(Math.floor((Date.now() - runStartRef.current) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [running]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter(c =>
      c.script.toLowerCase().includes(q) ||
      (c.title || '').toLowerCase().includes(q) ||
      (c.description || '').toLowerCase().includes(q)
    );
  }, [catalog, filter]);

  const currentEntry = useMemo(
    () => catalog.find(c => c.script === selected) || null,
    [catalog, selected]
  );

  const handleSelect = (entry) => {
    setSelected(entry.script);
    // Pre-fill catalog defaults + connected user's id_user for --user-id
    setArgValues(makeDefaults(entry, props.accountInformation));
    setResult(null);
    setConfirmDanger(false);
  };

  const handleArgChange = (flag, value) => {
    setArgValues(prev => ({ ...prev, [flag]: value }));
  };

  const handleRun = useCallback(async () => {
    if (!currentEntry) return;
    if (currentEntry.dangerous && !confirmDanger) return;

    // Strip empty values & convert booleans
    const args = {};
    for (const a of currentEntry.args) {
      const v = argValues[a.flag];
      if (a.type === 'boolean') {
        if (v === true) {
          args[a.flag] = true;
        } else if (v === false && a.defaultValue === true) {
          // Explicitly unchecked a default-on toggle — must tell the script it's off
          args[a.flag] = false;
        }
      } else if (a.type === 'multiselect') {
        // Send the checked option values as a CSV string (empty = omit the flag).
        if (Array.isArray(v) && v.length > 0) args[a.flag] = v.join(',');
      } else if (v !== undefined && v !== null && String(v).trim() !== '') {
        args[a.flag] = v;
      }
    }
    // If user checked the dangerous confirmation, automatically pass --confirm
    // so scripts that check process.stdin.isTTY don't block when run headless.
    if (currentEntry.dangerous && confirmDanger) {
      args['--confirm'] = true;
    }

    setRunning(true);
    setResult(null);
    try {
      const res = await DataServiceSQL.runAdminFixture(currentEntry.script, args);
      setResult(res.data);
    } catch (e) {
      const data = e && e.response && e.response.data;
      setResult({
        error: (data && data.error) || (e.message || 'Run failed'),
        exit_code: -1,
        stdout: '',
        stderr: '',
      });
    }
    setRunning(false);
  }, [currentEntry, argValues, confirmDanger]);

  const renderArgInput = (a) => {
    const v = argValues[a.flag];
    const common = {
      title: a.tooltip || '',
      style: { width: '100%', fontSize: '0.85em' },
      className: 'form-control form-control-sm',
      autoComplete: 'off',
    };
    if (a.type === 'boolean') {
      return (
        <label title={a.tooltip || ''} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0 }}>
          <input
            type="checkbox"
            checked={v === true}
            onChange={(e) => handleArgChange(a.flag, e.target.checked)}
          />
          <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: a.required ? '#c0392b' : '#333' }}>
            {a.label}{a.required ? ' *' : ''}
          </span>
          <span style={{ fontSize: '0.78em', color: '#999' }}>{a.flag}</span>
        </label>
      );
    }
    if (a.type === 'select') {
      return (
        <select {...common} value={v || ''} onChange={(e) => handleArgChange(a.flag, e.target.value)}>
          <option value=""></option>
          {(a.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (a.type === 'textarea') {
      return (
        <textarea
          title={a.tooltip || ''}
          className="form-control form-control-sm"
          rows={a.rows || 5}
          value={v === undefined ? '' : v}
          placeholder={a.placeholder || a.flag}
          onChange={(e) => handleArgChange(a.flag, e.target.value)}
          autoComplete="off"
          style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.8em' }}
        />
      );
    }
    if (a.type === 'multiselect') {
      const selected = Array.isArray(v) ? v : [];
      const opts = (a.options || []).map(o => (typeof o === 'string' ? { value: o, label: o } : o));
      const toggle = (val) => {
        const set = new Set(selected);
        if (set.has(val)) set.delete(val); else set.add(val);
        handleArgChange(a.flag, Array.from(set));
      };
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '2px 10px' }}>
          {opts.map(o => (
            <label
              key={o.value}
              title={o.tooltip || o.value}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, margin: 0, cursor: 'pointer', fontSize: '0.82em' }}
            >
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={() => toggle(o.value)}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      );
    }
    // A bounded number (min + max declared) is shown as a slider with an editable
    // number box next to it, so the value can be dragged or typed directly.
    if (a.type === 'number' && a.min !== undefined && a.max !== undefined) {
      const hasVal = v !== undefined && v !== null && v !== '';
      const fallback = a.defaultValue !== undefined ? a.defaultValue : a.min;
      const current = hasVal ? v : fallback;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="range"
            title={a.tooltip || ''}
            min={a.min}
            max={a.max}
            step={a.step || 1}
            value={current}
            onChange={(e) => handleArgChange(a.flag, e.target.value)}
            style={{ flex: 1, minWidth: 0 }}
          />
          <input
            type="number"
            title={a.tooltip || ''}
            min={a.min}
            max={a.max}
            step={a.step || 1}
            value={current}
            onChange={(e) => handleArgChange(a.flag, e.target.value)}
            className="form-control form-control-sm"
            style={{ width: 64, flex: '0 0 auto', fontSize: '0.85em' }}
            autoComplete="off"
          />
        </div>
      );
    }
    return (
      <input
        {...common}
        type={a.type === 'number' ? 'number' : 'text'}
        value={v === undefined ? '' : v}
        placeholder={a.flag}
        onChange={(e) => handleArgChange(a.flag, e.target.value)}
      />
    );
  };

  const renderCommandPreview = () => {
    if (!currentEntry) return '';
    const parts = [`node fixtures/${currentEntry.script}`];
    for (const a of currentEntry.args) {
      const v = argValues[a.flag];
      if (a.type === 'boolean') {
        if (v === true) parts.push(a.flag);
      } else if (a.type === 'multiselect') {
        if (Array.isArray(v) && v.length > 0) parts.push(a.flag, v.join(','));
      } else if (a.type === 'textarea') {
        // Multiline value would break the one-line preview — show a line count.
        const lines = String(v || '').split('\n').map(s => s.trim()).filter(Boolean);
        if (lines.length > 0) parts.push(a.flag, `"<${lines.length} ligne(s)>"`);
      } else if (v !== undefined && v !== null && String(v).trim() !== '') {
        const s = String(v);
        parts.push(a.flag, /\s/.test(s) ? `"${s}"` : s);
      }
    }
    return parts.join(' ');
  };

  const colorizeStdout = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      let color = '#ecf0f1';
      if (/^\[run\]/.test(line))    color = '#74b9ff';
      else if (/^\[ok\]/.test(line))    color = '#55efc4';
      else if (/^\[done\]/.test(line))  color = '#00b894';
      else if (/^\[error\]/.test(line)) color = '#ff7675';
      else if (/^\[warn\]/.test(line))  color = '#fdcb6e';
      else if (/^\[info\]/.test(line))  color = '#a29bfe';
      else if (/^──/.test(line))         color = '#636e72';
      return <span key={i} style={{ color, display: 'block' }}>{line || '\u00a0'}</span>;
    });
  };

  const handleTriggerAlert = async () => {
    if (!alertPatientId.trim() || !alertType) return;
    setAlertRunning(true);
    setAlertResult(null);
    try {
      const res = await DataServiceSQL.triggerAdminAlert(
        alertPatientId.trim(), alertType, alertValue.trim() || undefined,
        alertSendSms, alertSendMail, alertRecall, alertLang
      );
      setAlertResult({ ok: true, data: res.data });
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.error) || e.message || 'Erreur';
      setAlertResult({ ok: false, error: msg });
    }
    setAlertRunning(false);
  };

  const handleSendReminder = async () => {
    if (!reminderPatientId.trim() || !reminderType) return;
    setReminderRunning(true);
    setReminderResult(null);
    try {
      const res = await DataServiceSQL.sendAdminReminder(
        reminderPatientId.trim(), reminderType,
        reminderSendSms, reminderSendMail, reminderRecall, reminderLang
      );
      setReminderResult({ ok: true, data: res.data });
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.error) || e.message || 'Erreur';
      setReminderResult({ ok: false, error: msg });
    }
    setReminderRunning(false);
  };

  return (
    <div className={classes.popup_background} onClick={props.cancelPopup}>
      <div
        style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="management-menu-popup"
          style={{ height: 'auto', maxHeight: '94vh', display: 'flex', flexDirection: 'column', position: 'relative' }}
        >
          <button
            onClick={props.cancelPopup}
            style={{ position: 'absolute', top: 6, right: 20, zIndex: 10, background: '#fff', border: '1px solid #ccc', borderRadius: '50%', fontSize: 18, fontWeight: 'bold', lineHeight: 1, cursor: 'pointer', color: '#DB2828', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}
            title={intl.formatMessage({ id: 'button.close', defaultMessage: 'Fermer' })}
          >×</button>

          <Row style={{ paddingBottom: '8px', alignItems: 'center', flexShrink: 0 }}>
            <Col style={{ fontWeight: 'bold', fontSize: '1.7em', textAlign: 'center' }}>
              <Icon name="flask" />
              <FormattedMessage id="admin.fixtures.title" defaultMessage="Fixtures" />
            </Col>
          </Row>

          <Row style={{ flexShrink: 0, marginBottom: 6 }}>
            <Col style={{ textAlign: 'center', fontSize: '0.82em', color: '#666' }}>
              <FormattedMessage
                id="admin.fixtures.subtitle"
                defaultMessage="Exécution de scripts de fixtures backend. Réservé aux super-admins."
              />
            </Col>
          </Row>

          {/* Tab bar */}
          <Row style={{ flexShrink: 0, marginBottom: 10 }}>
            <Col style={{ display: 'flex', justifyContent: 'center', gap: 0 }}>
              {[
                { key: 'fixtures', label: 'Fixtures', icon: 'flask' },
                { key: 'actions',  label: 'Actions',  icon: 'bolt'  },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    padding: '5px 22px',
                    fontSize: '0.88em',
                    fontWeight: activeTab === tab.key ? 'bold' : 'normal',
                    background: activeTab === tab.key ? '#1a73e8' : '#f0f0f0',
                    color: activeTab === tab.key ? '#fff' : '#444',
                    border: '1px solid ' + (activeTab === tab.key ? '#1a73e8' : '#ccc'),
                    borderRadius: tab.key === 'fixtures' ? '4px 0 0 4px' : '0 4px 4px 0',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                >
                  <Icon name={tab.icon} style={{ marginRight: 5 }} />{tab.label}
                </button>
              ))}
            </Col>
          </Row>

          {loading && activeTab === 'fixtures' && (
            <Row><Col style={{ textAlign: 'center', padding: '20px' }}>
              <Icon loading name="spinner" /> <FormattedMessage id="admin.fixtures.loading" defaultMessage="Chargement…" />
            </Col></Row>
          )}

          {!!loadError && !loading && activeTab === 'fixtures' && (
            <Row><Col style={{ textAlign: 'center', padding: '20px', color: '#e74c3c' }}>{loadError}</Col></Row>
          )}

          {!loading && !loadError && activeTab === 'fixtures' && (
            <div style={{ display: 'flex', flexDirection: 'row', gap: 12, flex: 1, minHeight: 0 }}>
              {/* LEFT: catalog list */}
              <div style={{ flex: '0 0 32%', display: 'flex', flexDirection: 'column', minWidth: 240, borderRight: '1px solid #eee', paddingRight: 8 }}>
                <input
                  className="form-control form-control-sm"
                  placeholder={intl.formatMessage({ id: 'admin.fixtures.search', defaultMessage: 'Rechercher…' })}
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  autoComplete="off"
                  style={{ marginBottom: 8 }}
                />
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  {filtered.map(entry => {
                    const isSel = entry.script === selected;
                    return (
                      <div
                        key={entry.script}
                        onClick={() => handleSelect(entry)}
                        title={entry.description}
                        style={{
                          padding: '6px 8px',
                          marginBottom: 4,
                          borderRadius: 4,
                          cursor: 'pointer',
                          background: isSel ? '#1a73e8' : (entry.dangerous ? '#fff4f4' : '#f7f7f7'),
                          color: isSel ? '#fff' : '#222',
                          border: '1px solid ' + (isSel ? '#1a73e8' : '#e0e0e0'),
                          opacity: entry.available === false ? 0.5 : 1,
                        }}
                      >
                        <div style={{ fontWeight: 'bold', fontSize: '0.88em' }}>
                          {entry.dangerous ? '⚠ ' : ''}{entry.title}
                        </div>
                        <div style={{ fontSize: '0.72em', opacity: 0.85 }}>{entry.script}</div>
                      </div>
                    );
                  })}
                  {filtered.length === 0 && (
                    <div style={{ textAlign: 'center', color: '#999', padding: 12, fontSize: '0.85em' }}>
                      <FormattedMessage id="admin.fixtures.empty" defaultMessage="Aucune fixture." />
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT: detail / args / result */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
                {!currentEntry && (
                  <div style={{ color: '#777', textAlign: 'center', padding: 30 }}>
                    <FormattedMessage id="admin.fixtures.pick" defaultMessage="Sélectionnez une fixture à gauche." />
                  </div>
                )}

                {currentEntry && (
                  <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    {/* Scrollable args region — lets long forms (e.g. Create Demo
                        Patients) scroll while the Run button below stays visible. */}
                    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 6 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '1.05em' }}>
                        {currentEntry.dangerous ? '⚠ ' : ''}{currentEntry.title}
                        <span style={{ fontWeight: 'normal', color: '#888', fontSize: '0.78em', marginLeft: 8 }}>
                          {currentEntry.script}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.85em', color: '#444', margin: '6px 0 10px' }}>
                        {currentEntry.description}
                      </div>

                      {/* Arg fields — ungrouped args in the main grid, then each
                          declared `group` rendered inside its own titled box. */}
                      {(() => {
                        const gridStyle = {
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                          gap: '6px 12px',
                        };
                        const renderField = (a) => (
                          <div key={a.flag} style={{ display: 'flex', flexDirection: 'column', gridColumn: (a.type === 'multiselect' || a.type === 'textarea') ? '1 / -1' : undefined }}>
                            {a.type !== 'boolean' && (
                              <label
                                title={a.tooltip || ''}
                                style={{ fontSize: '0.78em', fontWeight: 'bold', marginBottom: 2, color: a.required ? '#c0392b' : '#333' }}
                              >
                                {a.label} {a.required ? '*' : ''}
                                <span style={{ color: '#999', fontWeight: 'normal', marginLeft: 4 }}>{a.flag}</span>
                              </label>
                            )}
                            {renderArgInput(a)}
                          </div>
                        );
                        // Use the arg's declared `group`, else fall back to a box for
                        // the device/module data toggles (--with-*) so they stay
                        // grouped even if the backend catalog predates the `group` field.
                        const groupOf = (a) => {
                          if (a.group) return a.group;
                          if (!a.flag) return null;
                          if (a.flag.indexOf('--with-') === 0) return 'Données à créer (devices & modules)';
                          if (a.flag.indexOf('--alert-') === 0) return 'Alertes';
                          return null;
                        };
                        const ungrouped = currentEntry.args.filter(a => !groupOf(a));
                        const groupNames = [];
                        const groups = {};
                        for (const a of currentEntry.args) {
                          const g = groupOf(a);
                          if (!g) continue;
                          if (!groups[g]) { groups[g] = []; groupNames.push(g); }
                          groups[g].push(a);
                        }
                        return (
                          <>
                            <div style={{ ...gridStyle, marginBottom: 8 }}>
                              {ungrouped.map(renderField)}
                            </div>
                            {groupNames.map(name => (
                              <fieldset key={name} style={{ border: '1px solid #d0d7e2', borderRadius: 6, padding: '6px 10px 10px', marginBottom: 8 }}>
                                <legend style={{ fontSize: '0.78em', fontWeight: 'bold', color: '#1a73e8', padding: '0 6px', width: 'auto', marginBottom: 0 }}>
                                  {name}
                                </legend>
                                <div style={gridStyle}>
                                  {groups[name].map(renderField)}
                                </div>
                              </fieldset>
                            ))}
                          </>
                        );
                      })()}
                    </div>

                    {/* Command preview + run controls — pinned below the scroll
                        area so they stay visible no matter how long the form is. */}
                    <div style={{ flexShrink: 0, marginTop: 8 }}>
                      {/* Command preview */}
                      <div style={{
                        fontFamily: 'monospace',
                        fontSize: '0.78em',
                        background: '#0f111a',
                        color: '#9efb9e',
                        padding: '6px 10px',
                        borderRadius: 4,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        marginBottom: 8,
                        maxHeight: 56,
                        overflowY: 'auto',
                      }}>
                        $ {renderCommandPreview()}
                      </div>

                      {/* Run controls */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                        {currentEntry.dangerous && (
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#c0392b', fontSize: '0.85em', margin: 0 }} title={intl.formatMessage({ id: 'admin.fixtures.danger.tooltip', defaultMessage: 'Cette fixture peut modifier ou supprimer des données. Confirmation requise.' })}>
                            <input
                              type="checkbox"
                              checked={confirmDanger}
                              onChange={(e) => setConfirmDanger(e.target.checked)}
                            />
                            <FormattedMessage id="admin.fixtures.danger.confirm" defaultMessage="Je confirme l'exécution destructive" />
                          </label>
                        )}
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={handleRun}
                          disabled={running || (currentEntry.dangerous && !confirmDanger)}
                          style={{ minWidth: 110 }}
                        >
                          {running
                            ? <><Icon loading name="spinner" /> <FormattedMessage id="admin.fixtures.running" defaultMessage="Exécution…" />{' '}<span style={{ fontSize: '0.88em', opacity: 0.9 }}>⏱ {elapsed}s</span></>
                            : <><Icon name="play" /> <FormattedMessage id="admin.fixtures.run" defaultMessage="Lancer" /></>
                          }
                        </button>
                        {result && (
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setResult(null)}
                            disabled={running}
                          >
                            <FormattedMessage id="admin.fixtures.clear_result" defaultMessage="Effacer résultat" />
                          </button>
                        )}
                      </div>

                      {/* Running status panel — shown while script executes */}
                      {running && (
                        <div style={{ fontSize: '0.82em', background: '#f0f4ff', border: '1px solid #c0d0ff', borderRadius: 4, padding: '6px 10px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Icon loading name="spinner" size="small" />
                          <span>
                            {currentEntry.script === 'create_demo_patients.js'
                              ? (elapsed < 5
                                  ? 'Création des patients…'
                                  : elapsed < 60
                                    ? 'Patients créés — remplissage des données (breathing, alertes)…'
                                    : 'Remplissage des données en cours (peut prendre 1-2 min)…')
                              : 'Script en cours d\'exécution…'
                            }
                          </span>
                          <b>⏱ {elapsed}s</b>
                        </div>
                      )}
                    </div>

                    {/* Result panel */}
                    {result && (
                      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        <div style={{
                          flexShrink: 0,
                          padding: '4px 8px',
                          fontSize: '0.82em',
                          background: result.error || result.exit_code !== 0 ? '#fdecea' : '#e8f5e9',
                          color: result.error || result.exit_code !== 0 ? '#c0392b' : '#2e7d32',
                          borderRadius: 4,
                          marginBottom: 4,
                        }}>
                          {result.error
                            ? <>❌ {result.error}</>
                            : <>
                                {result.exit_code === 0 ? '✅' : '❌'}{' '}
                                <FormattedMessage id="admin.fixtures.exit_code" defaultMessage="Exit code" />: <b>{result.exit_code}</b>
                                {result.signal ? <> · signal: {result.signal}</> : null}
                                {result.timed_out ? <> · ⏱ <FormattedMessage id="admin.fixtures.timed_out" defaultMessage="timeout" /></> : null}
                                {' · '}<FormattedMessage id="admin.fixtures.duration" defaultMessage="durée" />: {result.duration_ms} ms
                                {result.truncated ? <> · <FormattedMessage id="admin.fixtures.truncated" defaultMessage="sortie tronquée" /></> : null}
                              </>
                          }
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 4, minHeight: 0 }}>
                          {result.stdout !== undefined && (
                            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                              <div style={{ fontSize: '0.75em', fontWeight: 'bold', color: '#666' }}>stdout</div>
                              <pre style={{
                                flex: 1, minHeight: 0, overflow: 'auto', background: '#0f111a', color: '#ecf0f1',
                                padding: 8, borderRadius: 4, fontSize: '0.78em', margin: 0, whiteSpace: 'pre-wrap',
                            }}>{colorizeStdout(result.stdout)}</pre>
                            </div>
                          )}
                          {result.stderr ? (
                            <div style={{ flex: '0 0 30%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                              <div style={{ fontSize: '0.75em', fontWeight: 'bold', color: '#c0392b' }}>stderr</div>
                              <pre style={{
                                flex: 1, minHeight: 0, overflow: 'auto', background: '#1a0f0f', color: '#ffb3b3',
                                padding: 8, borderRadius: 4, fontSize: '0.78em', margin: 0, whiteSpace: 'pre-wrap',
                              }}>{result.stderr}</pre>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── ACTIONS TAB ── */}
          {activeTab === 'actions' && (
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 4px' }}>

              {/* ── Déclencher une alerte ── */}
              <fieldset style={{ border: '1px solid #d0d7e2', borderRadius: 6, padding: '10px 14px 14px', marginBottom: 16 }}>
                <legend style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#1a73e8', padding: '0 6px', width: 'auto', marginBottom: 0 }}>
                  <Icon name="warning sign" /> Déclencher une alerte
                </legend>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px 14px', marginBottom: 10 }}>
                  <div>
                    <label style={{ fontSize: '0.78em', fontWeight: 'bold', color: '#c0392b', display: 'block', marginBottom: 2 }}>
                      ID Patient <span style={{ color: '#999', fontWeight: 'normal' }}>(id_patient)</span>
                    </label>
                    <input
                      className="form-control form-control-sm"
                      placeholder="UUID du patient"
                      value={alertPatientId}
                      onChange={e => setAlertPatientId(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.78em', fontWeight: 'bold', color: '#333', display: 'block', marginBottom: 2 }}>
                      Type d'alerte
                    </label>
                    <select
                      className="form-control form-control-sm"
                      value={alertType}
                      onChange={e => setAlertType(e.target.value)}
                    >
                      {ALERT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.78em', fontWeight: 'bold', color: '#333', display: 'block', marginBottom: 2 }}>
                      Valeur (à stocker / envoyer)
                    </label>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Ex: 39.5"
                      value={alertValue}
                      onChange={e => setAlertValue(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 4 }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: 0, cursor: 'pointer', fontSize: '0.85em' }}>
                      <input type="checkbox" checked={alertSendSms} onChange={e => setAlertSendSms(e.target.checked)} />
                      Envoyer par SMS
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: 0, cursor: 'pointer', fontSize: '0.85em' }}>
                      <input type="checkbox" checked={alertSendMail} onChange={e => setAlertSendMail(e.target.checked)} />
                      Envoyer par Mail
                    </label>
                    <label
                      title="Ajoute le lien d'accusé de réception dans le mail/SMS (comme les alertes classiques, si la fonctionnalité de rappel est activée sur la structure)."
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: 0, cursor: 'pointer', fontSize: '0.85em' }}
                    >
                      <input type="checkbox" checked={alertRecall} onChange={e => setAlertRecall(e.target.checked)} />
                      Lien d'accusé de réception (recall)
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: 0, fontSize: '0.85em' }}>
                      Langue
                      <select
                        className="form-control form-control-sm"
                        style={{ width: 'auto', display: 'inline-block' }}
                        value={alertLang}
                        onChange={e => setAlertLang(e.target.value)}
                      >
                        <option value="fr-FR">Français</option>
                        <option value="en-GB">English</option>
                      </select>
                    </label>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={handleTriggerAlert}
                    disabled={alertRunning || !alertPatientId.trim()}
                    style={{ minWidth: 170 }}
                  >
                    {alertRunning
                      ? <><Icon loading name="spinner" /> Envoi en cours…</>
                      : <><Icon name="warning sign" /> Déclencher une alerte</>
                    }
                  </button>
                  {alertResult && (
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setAlertResult(null)}>
                      Effacer
                    </button>
                  )}
                </div>

                {alertResult && (
                  <div style={{
                    marginTop: 8, padding: '6px 10px', borderRadius: 4, fontSize: '0.82em',
                    background: alertResult.ok ? '#e8f5e9' : '#fdecea',
                    color: alertResult.ok ? '#2e7d32' : '#c0392b',
                    border: '1px solid ' + (alertResult.ok ? '#c8e6c9' : '#ffcdd2'),
                  }}>
                    {alertResult.ok ? (
                      <>
                        ✅ Alerte enregistrée — id: <b>{alertResult.data.alert_id}</b>
                        {' · '}mail: {alertResult.data.mail_sent ? '✔' : '✘'}
                        {' · '}SMS: {alertResult.data.sms_sent ? '✔' : '✘'}
                      </>
                    ) : (
                      <>❌ {alertResult.error}</>
                    )}
                  </div>
                )}
              </fieldset>

              {/* ── Envoyer un rappel de mesure ── */}
              <fieldset style={{ border: '1px solid #d0d7e2', borderRadius: 6, padding: '10px 14px 14px', marginBottom: 16 }}>
                <legend style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#1a73e8', padding: '0 6px', width: 'auto', marginBottom: 0 }}>
                  <Icon name="bell" /> Rappel de prise de mesure
                </legend>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px 14px', marginBottom: 10 }}>
                  <div>
                    <label style={{ fontSize: '0.78em', fontWeight: 'bold', color: '#c0392b', display: 'block', marginBottom: 2 }}>
                      ID Patient <span style={{ color: '#999', fontWeight: 'normal' }}>(id_patient)</span>
                    </label>
                    <input
                      className="form-control form-control-sm"
                      placeholder="UUID du patient"
                      value={reminderPatientId}
                      onChange={e => setReminderPatientId(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.78em', fontWeight: 'bold', color: '#333', display: 'block', marginBottom: 2 }}>
                      Type de mesure
                    </label>
                    <select
                      className="form-control form-control-sm"
                      value={reminderType}
                      onChange={e => setReminderType(e.target.value)}
                    >
                      {REMINDER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 4 }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: 0, cursor: 'pointer', fontSize: '0.85em' }}>
                      <input type="checkbox" checked={reminderSendSms} onChange={e => setReminderSendSms(e.target.checked)} />
                      Envoyer par SMS
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: 0, cursor: 'pointer', fontSize: '0.85em' }}>
                      <input type="checkbox" checked={reminderSendMail} onChange={e => setReminderSendMail(e.target.checked)} />
                      Envoyer par Mail
                    </label>
                    <label
                      title="Ajoute le lien d'accusé de réception dans le mail/SMS (comme les alertes classiques, si la fonctionnalité de rappel est activée sur la structure)."
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: 0, cursor: 'pointer', fontSize: '0.85em' }}
                    >
                      <input type="checkbox" checked={reminderRecall} onChange={e => setReminderRecall(e.target.checked)} />
                      Lien d'accusé de réception (recall)
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: 0, fontSize: '0.85em' }}>
                      Langue
                      <select
                        className="form-control form-control-sm"
                        style={{ width: 'auto', display: 'inline-block' }}
                        value={reminderLang}
                        onChange={e => setReminderLang(e.target.value)}
                      >
                        <option value="fr-FR">Français</option>
                        <option value="en-GB">English</option>
                      </select>
                    </label>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-sm btn-warning"
                    onClick={handleSendReminder}
                    disabled={reminderRunning || !reminderPatientId.trim()}
                    style={{ minWidth: 200 }}
                  >
                    {reminderRunning
                      ? <><Icon loading name="spinner" /> Envoi en cours…</>
                      : <><Icon name="bell" /> Envoyer un rappel de mesure</>
                    }
                  </button>
                  {reminderResult && (
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setReminderResult(null)}>
                      Effacer
                    </button>
                  )}
                </div>

                {reminderResult && (
                  <div style={{
                    marginTop: 8, padding: '6px 10px', borderRadius: 4, fontSize: '0.82em',
                    background: reminderResult.ok ? '#e8f5e9' : '#fdecea',
                    color: reminderResult.ok ? '#2e7d32' : '#c0392b',
                    border: '1px solid ' + (reminderResult.ok ? '#c8e6c9' : '#ffcdd2'),
                  }}>
                    {reminderResult.ok ? (
                      <>
                        ✅ Rappel envoyé — id: <b>{reminderResult.data.alert_id}</b>
                        {' · '}mail: {reminderResult.data.mail_sent ? '✔' : '✘'}
                        {' · '}SMS: {reminderResult.data.sms_sent ? '✔' : '✘'}
                      </>
                    ) : (
                      <>❌ {reminderResult.error}</>
                    )}
                  </div>
                )}
              </fieldset>

            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default withStyles(Constants.styles)(FixturesMenu);
